import React, {useCallback, useContext, useEffect, useMemo, useRef} from 'react';

import {
  Backdrop,
  CircularProgress,
  SvgIcon,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Stack,
} from '@zoom/react-ui';
import {addDays, format} from 'date-fns';
import {DateTime} from 'luxon';
import {useDispatch, useSelector} from 'react-redux';

import {
  DatesWindowContainer,
  NavButton,
} from './Style';

import {ReactComponent as ChevronLeftOutlined} from 'assets/svg/chevron-left-outlined.svg';
import {ReactComponent as ChevronLeftSmallOutlined} from 'assets/svg/chevron-left-small-outlined.svg';
import {ReactComponent as ChevronRightOutlined} from 'assets/svg/chevron-right-outlined.svg';
import {ReactComponent as ChevronRightSmallOutlined} from 'assets/svg/chevron-right-small-outlined.svg';
import {toDateTime} from 'Components/BookingBlocksWidget/utils';
import ApiContext from 'Components/Reschedulings/ApiContext';
import SlotSelector from 'Components/SlotSelector/index.js';
import {
  decrementSelectedDate,
  fetchApptAvailableTimes,
  incrementSelectedDate,
} from 'Store/BookAppointmentsStore.js';
import {
  expandRangeToWeeks,
  getEndOfWeek,
  getFirstDayOnCalendar,
  getLastDayOnCalendar,
  useQueryWithRetryCaptcha,
} from 'Utils';
import {
  getApptEndDate,
  getWeekAvailabilityKey,
  getStartDate,
  isTeamAppt,
  weekKeyToDt,
} from 'Utils/apptAvailabilityUtils';
import {useNumberOfColumns} from 'Utils/useSize';

export const useDisplayDatesWindows = (selectedDate, numberOfDateColumns) => {
  const apptDays = useSelector((state) => state.bookAppointments.apptDays);
  const weekendStatus = useSelector((state) => state.hostSettingsState?.weekendStatus);

  const datesWindow = useMemo(() => {
    if (!selectedDate || numberOfDateColumns <= 0) return [];

    // Create a Set of all available appointment date strings for quick lookup
    const availableDates = new Set(
      Object.values(apptDays)
        .flatMap(({days}) =>
          Array.isArray(days) ? days.map((d) => d.date) : []
        )
    );

    const result = [];
    let count = 0;
    let offset = 0;

    while (count < numberOfDateColumns) {
      const currentDate = addDays(selectedDate, offset);
      const formattedDate = format(currentDate, 'yyyy-MM-dd');

      // Check if the current date is a weekday (Monday to Friday)
      // getDay() returns 0 (Sunday) to 6 (Saturday)
      const isWeekday = ![0, 6].includes(currentDate.getDay());

      // If the current date is either an available appointment date or a weekday, include it
      if (availableDates.has(formattedDate) || isWeekday || weekendStatus) {
        result.push(currentDate);
        count++;
      }

      offset++;
    }

    return result;
  }, [selectedDate, numberOfDateColumns, apptDays, weekendStatus]);

  return datesWindow;
};

/**
 * Dates Window Component
 * Responsible for:
 * - controlling rendering more or less date slot columns depending on the window.
 * @param {Object} props
 * @return {ReactElement}
 */
export default function DatesWindow(props) {
  const {
    apptCap,
    bookedSlot,
    calendarId,
    hasTroubleShootingMode,
    isBookerVoting,
    // isHost,
    onSelectSlot,
    selectedSlot,
    timeZone,
    troubleShootingMode,
    voterInfo,
    votes,
  } = props;
  const containerRef = useRef();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const appt = useSelector((s) => s.bookAppointments.appt);
  const seqNo = useSelector((s) => s.bookAppointments.seqNo);
  // the selected date picker date as ISO string
  const selectedDate = useSelector((s) => s.bookAppointments.selectedDate);
  const selectedDay = toDateTime(selectedDate, timeZone, true);

  const votesArr = isBookerVoting ? Object.values(votes) : null;

  const calculateNumColumns = useCallback((el) => {
    if (isSmallScreen) {
      return 1;
    }
    const SHOW_MIN_COLUMNS = 1;
    try {
      const elWidth = el.current.offsetWidth;
      const MIN_COL_WIDTH = 150;
      const X_OFFSET = 160;
      return Math.max(SHOW_MIN_COLUMNS, Math.floor((elWidth - X_OFFSET) / MIN_COL_WIDTH));
    } catch {
      // fallback to showing 3
      return 3;
    }
  }, [isSmallScreen]);
  const numberOfDateColumns = useNumberOfColumns(containerRef, calculateNumColumns);
  const apptDays = useSelector((s) => s.bookAppointments.apptDays);
  const viewingMonth = useSelector((s) => s.bookAppointments.viewingMonth);

  const api = useContext(ApiContext);
  const apptsLoaderWrapped = useQueryWithRetryCaptcha(api.loadApptAvail, api?.captchaUser);
  const doFetchAvail = (api?.captchaUser) ? apptsLoaderWrapped : api.loadApptAvail;

  const datesWindow = useDisplayDatesWindows(new Date(selectedDate), numberOfDateColumns);

  const lastWindowDate = datesWindow[datesWindow.length-1];
  const weekKeys = useMemo(() => {
    const weekKeys = expandRangeToWeeks(
      toDateTime(datesWindow[0], timeZone, true),
      toDateTime(datesWindow[datesWindow.length-1], timeZone, true).endOf('day'),
      timeZone,
    )
      .filter((weekStart) => weekStart.diff(getApptEndDate(appt, timeZone)).milliseconds < 0)
      .map((weekStart) => getWeekAvailabilityKey(weekStart, timeZone));
    return Array.from(new Set(weekKeys));
  }, [appt, timeZone, datesWindow]);

  const isLoadingTimes = weekKeys.some(
    (k) => !apptDays?.[k] || (apptDays?.[k]?.loading && !apptDays?.[k]?.days && !apptDays?.[k]?.error));

  const nextMonthWeekKeys = useMemo(() => weekKeys.filter((weekKey) => {
    // DatesWindow only try to fetch missing weeks of the next month if it is not already loaded or pending
    const weekStart = weekKeyToDt(weekKey);
    return weekStart.diff(
      getEndOfWeek(toDateTime(viewingMonth, timeZone, true).endOf('month'))
    ).milliseconds > 0;
  }), [timeZone, viewingMonth, weekKeys]);

  useEffect(() => {
    if (appt) {
      const missingWeekKeys = nextMonthWeekKeys.filter((weekKey) => {
        const entry = apptDays?.[weekKey];
        if (!entry || (!entry.days && !entry.loading && !entry.error)) {
          return true;
        }
        return false;
      });
      for (const weekKey of missingWeekKeys) {
        dispatch(fetchApptAvailableTimes({
          fetchApptAvailQuery: {
            query: doFetchAvail,
            args: [
              calendarId,
              appt.id,
              {
                timeMin: weekKeyToDt(weekKey).toUTC().toISO(),
                timeMax: weekKeyToDt(weekKey).plus({week: 1}).toUTC().toISO(),
                timeZone: timeZone,
              },
            ],
          },
          seqNo: seqNo,
        }));
      }
    }
  }, [dispatch, seqNo, appt, calendarId, timeZone, apptDays, datesWindow, doFetchAvail,
    lastWindowDate, nextMonthWeekKeys]);

  // Date window navigation
  const nowDate = DateTime.now().setZone(timeZone).startOf('day');
  // Calculate previous button related values
  const currFirstDate = selectedDay.startOf('day');
  const apptStartDate = toDateTime(getStartDate(appt), timeZone);
  let prevFirstDate = currFirstDate.minus({days: numberOfDateColumns});
  prevFirstDate = DateTime.max(
    prevFirstDate,
    apptStartDate.startOf('day'),
    nowDate,
  );
  const daysFromPrevFirstDate = currFirstDate.diff(prevFirstDate, 'days').days;
  // differenceInCalendarDays(currFirstDate, prevFirstDate);
  const canNavPrev = daysFromPrevFirstDate > 0;

  const month = useSelector((s) => s.bookAppointments.viewingMonth);
  const monthWeekKeys = expandRangeToWeeks(
    getFirstDayOnCalendar(month, timeZone),
    getLastDayOnCalendar(month, timeZone),
    timeZone
  )
    .filter((weekStart) => weekStart.diff(getApptEndDate(appt, timeZone)).milliseconds < 0)
    .map((dt) => getWeekAvailabilityKey(dt, timeZone));

  const monthIsLoading = (monthWeekKeys.some((key) =>
    apptDays?.[key]?.error ||
    apptDays?.[key]?.loading
  ));

  // Calculate next button related values
  const currLastDate = currFirstDate.plus({days: numberOfDateColumns-1});
  const apptEndDate = getApptEndDate(appt, timeZone);
  let nextLastDate = currLastDate.plus({days: numberOfDateColumns});
  let daysTillNextLastDate = numberOfDateColumns;
  let canNavNext = true;
  let canNavNextMonth = true;
  if (apptEndDate) {
    nextLastDate = DateTime.min(
      nextLastDate,
      apptEndDate.startOf('day'),
    );
    daysTillNextLastDate = nextLastDate.diff(currLastDate, 'days').days;
    canNavNext = daysTillNextLastDate > 0;
  }

  if (canNavNext && (monthIsLoading && isTeamAppt(appt))) {
    nextLastDate = DateTime.min(
      nextLastDate,
      toDateTime(month, timeZone, true).endOf('month').startOf('day'),
    );
    nextLastDate = nextLastDate.diff(currLastDate, 'days').days;
    canNavNextMonth = nextLastDate > 0;
  }

  return (
    <Stack sx={{flexGrow: 1, minHeight: '85%'}}>
      <DatesWindowContainer
        ref={containerRef}
      >
        {selectedDate &&
      <>
        <NavButton
          aria-label="Previous Days"
          key="nav-prev-days"
          variant="outlined" size="medium" color="inherit"
          disabled={!canNavPrev}
          sx={!canNavPrev ? {
            opacity: 0.3,
          } : null}
          onClick={() => dispatch(decrementSelectedDate(daysFromPrevFirstDate))}
        >
          <SvgIcon
            fontSize="small"
            component={isSmallScreen ? ChevronLeftOutlined : ChevronLeftSmallOutlined}
            inheritViewBox
            sx={isSmallScreen ?
              {
                fontSize: '14px',
              } : null
            }
          />
        </NavButton>
        {datesWindow.map((date) =>
          <SlotSelector
            key={date.valueOf()}
            calendarId={calendarId}
            isLoading={isLoadingTimes}
            isBookerVoting={isBookerVoting}
            onSelectSlot={onSelectSlot}
            slotsForDate={date}
            selectedDate={selectedDate}
            selectedSlot={selectedSlot}
            selectedSlots={isBookerVoting ? votesArr : null}
            bookedSlot={bookedSlot}
            durationMinutes={appt.duration}
            apptCap={apptCap}
            timeZone={timeZone}
            voterInfo={voterInfo}
            troubleShootingMode={troubleShootingMode}
            hasTroubleShootingMode={hasTroubleShootingMode}
          />
        )}
        <NavButton
          aria-label="Next Days"
          key="nav-next-days"
          variant="outlined" size="medium" color="inherit"
          disabled={!canNavNext || !canNavNextMonth}
          sx={!canNavNext || !canNavNextMonth ? {
            opacity: 0.3,
          } : null}
          onClick={() => dispatch(incrementSelectedDate(daysTillNextLastDate))}
        >
          <SvgIcon
            fontSize="small"
            component={isSmallScreen ? ChevronRightOutlined : ChevronRightSmallOutlined}
            inheritViewBox
            sx={{
              fontSize: isSmallScreen? '14px' : null,
            }}
          />
          {!canNavNextMonth &&
            <Backdrop
              sx={{
                position: 'absolute',
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
              }}
              open={true}
            >
              <CircularProgress color="inherit" size={20} />
            </Backdrop>
          }
        </NavButton>
      </>
        }
      </DatesWindowContainer>
    </Stack>
  );
}
